/**
 * Hooks de dados para a página de Relatórios & Analytics.
 *
 * Estratégia: consultas diretas ao Supabase (sem RPC nova) sempre que possível.
 * contact_geo_summary() é reutilizado do módulo geo.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

// ── Crescimento de contatos (últimos 6 meses) ─────────────────────────────────

export interface GrowthPoint {
  label: string;   // 'jan', 'fev', etc.
  value: number;   // novos contatos nesse mês
  total: number;   // total acumulado até o final do mês
}

export function useContactGrowth() {
  return useQuery<GrowthPoint[]>({
    queryKey: ['analytics', 'contact-growth'],
    queryFn: async (): Promise<GrowthPoint[]> => {
      // Pega todos os contatos com data de criação para agrupar client-side
      const now          = new Date();
      const sixMoAgo     = new Date(now.getFullYear(), now.getMonth() - 5, 1);

      // Consulta 1: novos por mês (contatos criados nos últimos 6 meses)
      const { data: rows } = await supabase
        .from('contacts')
        .select('created_at')
        .gte('created_at', sixMoAgo.toISOString());

      // Consulta 2: total antes do período (para calcular acumulado)
      const { count: baseBefore } = await supabase
        .from('contacts')
        .select('*', { count: 'exact', head: true })
        .lt('created_at', sixMoAgo.toISOString());

      // Gera os 6 meses
      const months: { label: string; start: Date; end: Date }[] = [];
      for (let i = 5; i >= 0; i--) {
        const start = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const end   = new Date(now.getFullYear(), now.getMonth() - i + 1, 1);
        months.push({
          label: start.toLocaleDateString('pt-BR', { month: 'short' }),
          start,
          end,
        });
      }

      // Agrupa por mês
      const byMonth: Record<string, number> = {};
      for (const row of rows ?? []) {
        const d    = new Date(row.created_at as string);
        const key  = `${d.getFullYear()}-${d.getMonth()}`;
        byMonth[key] = (byMonth[key] ?? 0) + 1;
      }

      let runningTotal = baseBefore ?? 0;
      return months.map(({ label, start, end: _end }) => {
        const key   = `${start.getFullYear()}-${start.getMonth()}`;
        const value = byMonth[key] ?? 0;
        runningTotal += value;
        return { label, value, total: runningTotal };
      });
    },
    staleTime: 10 * 60_000,
  });
}

// ── Top bairros ───────────────────────────────────────────────────────────────

export interface NeighborhoodCount {
  neighborhood: string;
  count:        number;
}

export function useTopNeighborhoods(limit = 8) {
  return useQuery<NeighborhoodCount[]>({
    queryKey: ['analytics', 'top-neighborhoods', limit],
    queryFn: async (): Promise<NeighborhoodCount[]> => {
      const { data } = await db.rpc('contact_geo_summary', { p_group_by: 'neighborhood' });

      const rows = (data ?? []) as { neighborhood: string | null; count: number }[];
      return rows
        .filter((r) => r.neighborhood)
        .sort((a, b) => b.count - a.count)
        .slice(0, limit)
        .map((r) => ({ neighborhood: r.neighborhood!, count: r.count }));
    },
    staleTime: 10 * 60_000,
  });
}

// ── Performance de campanhas (últimas N campanhas WhatsApp disparadas) ─────────

export interface CampaignPerf {
  id:        string;
  name:      string;
  sent:      number;
  delivered: number;
  read:      number;
  date:      string; // 'dd/mm'
}

export function useCampaignPerformance(limit = 8) {
  return useQuery<CampaignPerf[]>({
    queryKey: ['analytics', 'campaign-performance', limit],
    queryFn: async (): Promise<CampaignPerf[]> => {
      const { data } = await db
        .from('campaigns')
        .select('id, name, stats_sent, stats_delivered, stats_read, started_at')
        .eq('type', 'whatsapp')
        .in('status', ['completed', 'running', 'paused'])
        .not('started_at', 'is', null)
        .order('started_at', { ascending: false })
        .limit(limit);

      return ((data ?? []) as {
        id: string; name: string;
        stats_sent: number; stats_delivered: number; stats_read: number;
        started_at: string;
      }[]).map((r) => ({
        id:        r.id,
        name:      r.name,
        sent:      r.stats_sent      ?? 0,
        delivered: r.stats_delivered ?? 0,
        read:      r.stats_read      ?? 0,
        date:      new Date(r.started_at).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
      })).reverse(); // cronológico
    },
    staleTime: 5 * 60_000,
  });
}

// ── Totais rápidos para KPIs ──────────────────────────────────────────────────

export interface AnalyticsKpis {
  campaignsThisMonth:   number;
  messagesThisMonth:    number;
  avgDeliveryRate:      number; // 0–100
  eventsThisMonth:      number;
}

export function useAnalyticsKpis() {
  return useQuery<AnalyticsKpis>({
    queryKey: ['analytics', 'kpis'],
    queryFn: async (): Promise<AnalyticsKpis> => {
      const now        = new Date();
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();

      const [campRes, evtRes] = await Promise.all([
        db
          .from('campaigns')
          .select('stats_sent, stats_delivered')
          .eq('type', 'whatsapp')
          .in('status', ['completed', 'running'])
          .gte('started_at', monthStart),
        supabase
          .from('events')
          .select('*', { count: 'exact', head: true })
          .gte('starts_at', monthStart),
      ]);

      const camps      = (campRes.data ?? []) as { stats_sent: number; stats_delivered: number }[];
      const totalSent  = camps.reduce((acc, c) => acc + (c.stats_sent      ?? 0), 0);
      const totalDeliv = camps.reduce((acc, c) => acc + (c.stats_delivered ?? 0), 0);

      return {
        campaignsThisMonth: camps.length,
        messagesThisMonth:  totalSent,
        avgDeliveryRate:    totalSent > 0 ? Math.round((totalDeliv / totalSent) * 100) : 0,
        eventsThisMonth:    evtRes.count ?? 0,
      };
    },
    staleTime: 5 * 60_000,
  });
}
